import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getListings, getPublicListings, Listing, storeBadge } from '../lib/listings';
import { thumbUrl } from '../lib/images';
import LikeButton from '../components/LikeButton';
import { getBrandImageUrl } from '../lib/brandImages';
import toast from 'react-hot-toast';
import { getIsAuthenticated } from '../lib/auth';
import SEO from '../components/SEO';

function Home() {
  const [listings, setListings] = useState([]);
  const [openBox, setOpenBox] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    checkAuthAndFetchListings();
    fetchOpenBox();
  }, []);

  const fetchOpenBox = async () => {
    // Public, PII-safe RPC; store (ClearanceWigs) open-box deals for everyone.
    const { listings: deals } = await getPublicListings(
      { source: 'clearancewigs', sort: 'newest' },
      { limit: 8 }
    );
    setOpenBox(deals || []);
  };

  const checkAuthAndFetchListings = async () => {
    try {
      const authenticated = await getIsAuthenticated();
      setIsAuthenticated(authenticated);
    } catch (err) {
      console.error('Error checking authentication:', err);
    }
    // Fetch the catalog for everyone — logged-out visitors read the public,
    // PII-safe RPC path (Phase 1 de-gate).
    fetchListings();
  };

  const fetchListings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { listings: fetchedListings, error } = await getListings({ limit: 24 });

      if (error) {
        console.error('Error fetching listings:', error);
        throw error;
      }

      setListings(fetchedListings || []);
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError('Failed to load listings. Please try again later.');
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO canonical="/" />
      {/* Hero Section */}
      <div className="relative rounded-2xl overflow-hidden mb-12">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
        <img
          src="/neon_hero_wigconnection.webp"
          alt="Sell your wigs and earn toward a new one at MyHairMail"
          className="w-full h-[440px] md:h-[600px] object-cover object-[center_center]"
        />
        <div className="absolute inset-0 z-20 flex items-end">
          <div className="max-w-2xl p-8 sm:p-12 pb-12 sm:pb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 drop-shadow-lg leading-tight">
              Sell a wig you love,<br />earn $15 toward a new one
            </h1>
            <p className="text-xl text-white mb-8 drop-shadow-md">
              List for free, connect with a buyer, and earn a{' '}
              <span className="font-bold text-rose-300">$15 MyHairMail reward</span> with every
              completed sale &mdash; plus hundreds of pre-owned &amp; open-box wigs from $74.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="bg-rose-600 hover:bg-rose-700 text-white px-8 py-3 rounded-full inline-block shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-150 font-medium"
              >
                Get Started for Free
              </Link>
              <Link
                to="/search"
                className="bg-white/95 hover:bg-white text-primary-700 px-8 py-3 rounded-full inline-block shadow-lg transition-colors font-medium"
              >
                Browse Wigs
              </Link>
              <Link
                to="/how-it-works"
                className="bg-white/10 hover:bg-white/20 text-white border border-white/50 px-8 py-3 rounded-full inline-block backdrop-blur-sm transition-colors font-medium"
              >
                How It Works
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Section - shown to authenticated users with no listings */}
      {isAuthenticated === true && listings.length === 0 && (
        <div className="max-w-4xl mx-auto py-12 px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Turn Unworn Wigs Into Money Toward New Ones</h2>

            <p className="text-lg text-gray-600 mb-8 text-center">
              WigConnection.com is the members-only marketplace where wig lovers buy, sell, and trade premium brand wigs — and earn a $15 MyHairMail reward for every completed sale.
            </p>

            <p className="text-gray-600 mb-8">
              List your gently used wigs for free, connect with a real buyer through secure messaging, and once the sale is confirmed we send you a $15 code toward your next favorite at MyHairMail. It's the wig community that gives back.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-primary-600 mt-1">→</div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900">All Members Verified</h3>
                    <p className="text-gray-600">No more anonymous transactions. Our subscription model and identity verification eliminate scammers and ensure you're dealing with real enthusiasts.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-primary-600 mt-1">→</div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900">Premium Brands Only</h3>
                    <p className="text-gray-600">Find quality pieces from Jon Renau, Ellen Wille, Raquel Welch, Gabor, and more without wading through counterfeits or low-quality options.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-primary-600 mt-1">→</div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900">Secure Transactions</h3>
                    <p className="text-gray-600">Our platform provides safe communication, user ratings, and transaction history so you can buy, sell, and trade with confidence.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-primary-600 mt-1">→</div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900">Earn $15 On Every Sale</h3>
                    <p className="text-gray-600">Complete a buyer-confirmed sale and earn a $15 discount code toward a new wig at MyHairMail — a thank-you for giving a beautiful wig a second life.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center space-y-6">
              <p className="text-gray-600">
                <strong>A True Community:</strong> Connect with fellow wig enthusiasts who understand the value of quality pieces and treat each transaction with respect.
              </p>

              <p className="text-lg font-medium text-gray-900">
                List a wig, find a deal, and start earning toward your next favorite.
              </p>

              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/register"
                  className="inline-block px-8 py-3 bg-primary-600 text-white font-medium rounded-full hover:bg-primary-700 transform hover:-translate-y-0.5 transition-all duration-150 shadow-lg hover:shadow-xl"
                >
                  JOIN NOW
                </Link>
                <Link
                  to="/how-it-works"
                  className="inline-block px-8 py-3 bg-white text-primary-700 font-medium rounded-full border border-primary-200 hover:bg-primary-50 transition-colors"
                >
                  How It Works
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ClearanceWigs deals row — official store inventory (open-box + new-at-outlet) */}
      {openBox.length > 0 && (
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Outlet &amp; open-box deals</h2>
            <Link to="/search?source=clearancewigs" className="text-sm font-medium text-rose-600 hover:text-rose-700">
              See all store deals &rarr;
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {openBox.map((listing) => (
              <Link
                key={listing.id}
                to={`/listing/${listing.slug || listing.id}`}
                className="bg-white rounded-lg shadow-md overflow-hidden transform hover:scale-[1.02] transition-transform duration-300"
              >
                <div className="relative pb-[100%]">
                  <img
              loading="lazy"
              decoding="async"
              src={thumbUrl(listing.image_urls?.[0] || getBrandImageUrl(listing.brand), 600)}
                    alt={listing.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getBrandImageUrl(listing.brand);
                    }}
                  />
                  <div className="absolute top-2 left-2 bg-rose-600 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full shadow">
                    {storeBadge(listing) ?? 'Deal'}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-sm text-gray-500 mb-1">{listing.brand}</div>
                  <h3 className="font-medium mb-2 line-clamp-1">{listing.title}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">${listing.price}</span>
                    {listing.original_price ? (
                      <span className="text-sm text-gray-400 line-through">${listing.original_price}</span>
                    ) : null}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products - shown to everyone (public catalog) */}
      {(
        <section>
          <h2 className="text-2xl font-bold mb-6">Latest Listings</h2>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm p-6">
              <p className="text-gray-500">{error}</p>
              <button
                onClick={fetchListings}
                className="mt-4 text-primary-600 hover:text-primary-700 px-4 py-2 border border-primary-200 rounded-md"
              >
                Try Again
              </button>
            </div>
          ) : listings.length === 0 ? (
            null
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {listings.map((listing: any) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.slug || listing.id}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden transform hover:scale-[1.02] transition-transform duration-300"
                >
                  <div className="relative pb-[100%]">
                    <img
              loading="lazy"
              decoding="async"
              src={thumbUrl(listing.image_urls?.[0] || getBrandImageUrl(listing.brand), 600)}
                      alt={listing.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getBrandImageUrl(listing.brand);
                      }}
                    />
                    <div className="absolute top-2 right-2">
                      <LikeButton
                        listingId={listing.id}
                        className="bg-white rounded-full p-1.5 shadow-md"
                      />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-sm text-gray-500 mb-1">{listing.brand}</div>
                    <h3 className="font-medium mb-2">{listing.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold">${listing.price}</span>
                      <span className="text-sm text-gray-600">{listing.condition}</span>
                    </div>
                    <div className="mt-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${listing.type === 'sale' ? 'bg-green-100 text-green-800' :
                          listing.type === 'trade' ? 'bg-blue-100 text-blue-800' :
                            'bg-purple-100 text-purple-800'
                        }`}>
                        {listing.type === 'sale' ? 'For Sale' :
                          listing.type === 'trade' ? 'For Trade' :
                            'Sale or Trade'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}

export default Home;